<template>
  <div class="project-hero-section" :class="[$config.platform, $config.project]">
    <div class="container">
      <div class="row hero">
        <div class="col col-7">
          <h1 class="project-hero-title h3">
            {{ project.title }}
          </h1>
          <p class="project-subtitle">
            {{ project.subtitle }}
            <Badge v-for="(badge, i) in project.badges" :key="i" :name="badge" />
          </p>
          <p class="project-hero-instructor">
            With {{ project.author }}
          </p>
          <div class="project-info">
            <p v-if="project.subtype">
              Type: <b>{{ project.subtype }} project</b>
            </p>
            <p v-if="duration">
              Duration: <b>{{ duration }}</b>
            </p>
            <p v-if="project.status && project.status.key !== 'not-started'" class="status">
              Status: <b>{{ project.status.name }}</b><CheckIcon v-if="project.status.key === 'done'" />
            </p>
          </div>
        </div>
        <div class="project-buttons">
          <nuxt-link v-if="$config.project === 'site'" class="btn-primary btn-lg" to="/signup/">
            Start Project
          </nuxt-link>
          <template v-else>
            <nuxt-link v-if="project.locked" class="btn btn-primary" to="/pricing/">
              Upgrade to Unlock
            </nuxt-link>
            <template v-else>
              <button
                class="btn"
                :class="button1.class"
                @click="startProject"
              >
                {{ button1.text }}
              </button>
              <button
                v-if="button2"
                class="btn"
                :class="button2.class"
                @click="$emit('download', button2.downloadKey)"
              >
                {{ button2.text }}
                <component :is="button2.icon" />
              </button>
            </template>
            <nuxt-link
              v-if="project.courseSlug && project.fromCourses"
              :to="`/courses/${project.courseSlug}/`"
              class="btn btn-text"
            >
              Back to course
            </nuxt-link>
          </template>

          <p v-if="$config.project === 'lms' && project.locked" class="not-subscribed-text">
            *Your current plan does not allow access to this project. Upgrade to unlock.
          </p>
        </div>
      </div>
    </div>
    <div v-if="project.image" class="wall" :style="{ 'background-image': `url('${$config.imagesUrl}/projects/${project.image}')` }" />
  </div>
</template>

<script>
import Badge from './Badge';
import IconProjectFiles from '../assets/svg/icon-project-files.svg?vue-component';
import IconProjectSolutionFiles from '../assets/svg/icon-project-solution-files.svg?vue-component';
import CheckIcon from '../assets/svg/icon-check-projects.svg?vue-component';

export default {
  components: {
    Badge, IconProjectFiles, IconProjectSolutionFiles, CheckIcon,
  },
  props: {
    project: { type: Object, default: () => ({}), required: true },
  },
  computed: {
    duration() {
      if (this.project.duration < 1) {
        return ' < 1 Hour';
      }

      return `${this.project.duration} Hour${this.project.duration === 1 ? '' : 's'}`;
    },
    button1() {
      if (this.project.status.key === 'not-started') {
        return {
          text: 'Start Project',
          class: 'btn-primary',
        };
      }

      if (this.project.status.key === 'in-progress') {
        return {
          text: 'Continue Project',
          class: 'btn-primary',
        };
      }

      return {
        text: 'View Instructions',
        class: 'btn-outline-dark-light-4',
      };
    },
    button2() {
      if (this.project.files === 0) {
        return false;
      }

      if (this.project.status.key === 'done' && this.project.type !== 'portfolio') {
        return {
          text: 'All solution files',
          class: 'btn-primary all-solution',
          icon: 'IconProjectSolutionFiles',
          downloadKey: 'solution-files',
        };
      }

      return {
        text: 'Project files',
        class: 'btn-outline-dark-light-4',
        icon: 'IconProjectFiles',
        downloadKey: 'project-files',
      };
    },
  },
  methods: {
    async startProject() {
      let sequence = null;

      if (this.project.status.key === 'not-started') {
        sequence = await this.$axios.$post(`/projects/${this.project.id}/sequence`);

        this.$emit('start', sequence);
      } else {
        sequence = await this.$axios.$get(`/projects/${this.project.id}/sequence`);
      }

      this.$store.dispatch('modals/open', { name: 'project-sequence', sequence: { ...sequence, slug: this.$route.params.slug } });
    },
  },
};
</script>

<style lang="scss" scoped>
.project-hero-section {
  display: flex;
  align-items: center;
  height: 544px;
  color: var(--main-light);
  position: relative;
  background: var(--main-dark);

  .container {
    position: relative;
    z-index: 1;
  }

  &.site {
    height: 650px;

    .project-hero-title {
      font-size: 40px;
    }

    .project-buttons {
      .btn-big {
        width: 300px;
        height: 58px;
        line-height: 58px;
        padding: 0;
      }
    }
  }

  .hero {
    display: grid;
  }

  .project-hero-title {
    margin-bottom: var(--spacing-12);
    font-weight: 600;
  }

  .project-hero-instructor {
    font-weight: 600;
    margin-top: 0;
    margin-bottom: var(--spacing-40);
  }

  .project-subtitle {
    margin: 0 0 var(--spacing-28);
    font-weight: 500;
    line-height: 125%;
    font-size: 28px;
    color: var(--main-light-shade-3);

    .library-badge {
      vertical-align: middle;
      margin-left: 4px;
    }
  }

  .project-info {
    color: var(--main-light-shade-4);
    margin-top: 0;

    p {
      margin-top: 0;
      margin-bottom: var(--spacing-8);

      &:last-of-type {
        margin-bottom: 0;
      }
    }

    b {
      color: var(--main-light);
    }

    .status {
      svg {
        width: 12px;
        margin-left: var(--spacing-4);

        path {
          fill: var(--main-light);
        }
      }
    }
  }

  .project-buttons {
    margin-top: 40px;
    padding-left: 15px;

    * + * {
      margin-left: var(--spacing-8);
    }

    .btn {
      svg {
        margin-left: var(--spacing-8);
      }
    }

    .btn-outline-dark-light-4 {
      &:hover {
        border-color: var(--accent);
        background: transparent;
        color: var(--accent);

        svg path {
          &[fill] {
            fill: var(--accent);
          }

          &[stroke] {
            stroke: var(--accent);
          }
        }
      }

      &:active {
        border-color: var(--accent);
        background: var(--accent);
        color: var(--true-white);

        svg path {
          &[fill] {
            fill: var(--true-white);
          }

          &[stroke] {
            stroke: var(--true-white);
          }
        }
      }
    }

    .btn-primary {
      padding-left: 12px;
      padding-right: 12px;

      svg {
        width: 20px;

        path[stroke] {
          stroke: var(--true-white);
        }
      }
    }

    .btn-text {
      color: var(--accent);

      &:hover {
        color: var(--accent-shade-1);
      }

      &:active {
        color: var(--accent-shade-2);
      }
    }

    .not-subscribed-text {
      font-size: 14px;
      line-height: 23px;
      color: var(--main-light-shade-4);
      margin: var(--spacing-12) 0 0;
      width: 50%;
    }
  }

  .wall {
    position: absolute;
    right: 0;
    top: 0;
    height: 100%;
    left: 0;
    z-index: 0;
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
    image-rendering: -webkit-optimize-contrast;
  }
}

@include bp($bp-laptop) {
  .project-hero-section {
    &.site {
      .project-hero-title {
        font-size: 32px;
      }

      .project-buttons .btn-big {
        width: 225px;
      }
    }

    .project-hero-title {
      font-size: 32px;
      line-height: 125%;
    }

    .project-subtitle {
      font-size: 24px;
      line-height: 125%;
    }
  }
}

@include bp($bp-phone) {
  .project-hero-section {
    height: auto !important;

    &.site {
      .project-hero-title {
        font-size: 32px;
        padding-top: var(--spacing-40);
      }

      .project-buttons .btn-big {
        width: 100%;
      }
    }

    .project-hero-title {
      font-size: 32px;
      line-height: 125%;
    }

    .project-subtitle {
      font-size: 24px;
      line-height: 125%;
    }

    .project-buttons {
      display: grid;
      padding: 0 12px;

      .btn {
        width: 100%;
        height: 52px;
        max-height: 52px;
        margin-left: 0;
        margin-right: 0;
        margin-bottom: var(--spacing-12);

        &.all-solution {
          order: -1;
        }
      }

      .not-subscribed-text {
        width: 100%;
      }
    }

    &.ds {
      background: var(--main-dark);
    }

    .container {
      padding-top: 370px;
      padding-bottom: 60px;
    }

    .wall {
      height: 370px;
      background-position: 72% center;

      &:before {
        content: "";
        background: linear-gradient(0deg, var(--main-dark) 0%, var(--main-dark) 0.01%, rgba(0, 12, 31, 0) 100%);
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        height: 98px;
      }
    }
  }
}

@include bp($bp-tablet) {
  .project-hero-section {
    .row {
      margin-left: -12px;
      margin-right: -12px;
    }

    .project-buttons {
      padding-left: 12px;
    }
  }
}

.fa .project-hero-section {
  .project-buttons {
    svg {
      path {
        &[fill] {
          fill: var(--main-light-shade-4);
        }

        &[stroke] {
          stroke: var(--main-light-shade-4);
        }
      }
    }

    .btn-primary {
      svg path[stroke] {
        stroke: var(--true-white);
      }
    }
  }
}
</style>
